/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useState, useEffect } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { fetchExternalText } from 'modules/common/utils/external-content';
import { isRequestCanceled } from 'modules/common/utils/request';
import { useSelector } from 'react-redux';
import {
  selectEnableHotelGroups,
  selectHotelName,
  selectIsHistoricalViOverlayActive,
  selectSystemInformation,
} from 'modules/dashboard/selectors';
import { isEmpty } from 'modules/common/helpers/object';
import { HISTORICAL_VI_BLUR_STYLE } from './style';
/**
 * Announcement bar component that uses across modules for display system notices
 */
const AnnouncementBlock = () => {
  const selectedHotelName = useSelector(selectHotelName);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const systemInfo = useSelector(selectSystemInformation);
  const isHistoricalViOverlayActive = useSelector(selectIsHistoricalViOverlayActive);
  //
  const [content, setContent] = useState(null);
  const [styles, setStyles] = useState('');
  const [noOfRooms, setNoOfRooms] = useState(0);
  //
  useEffect(() => {
    setNoOfRooms(0);
    if (selectedHotelName !== null && enableHotelGroups) {
      selectedHotelName?.hotels?.forEach((hotel) => {
        setNoOfRooms((prev) => prev + parseInt(hotel?.roomCapacity, 10));
      });
    } else if (selectedHotelName !== null) {
      setNoOfRooms((prev) => prev + parseInt(selectedHotelName?.roomCapacity, 10));
    }
  }, [selectedHotelName, enableHotelGroups]);
  //
  const getHtmlContent = async (blobUrl, signal) => {
    try {
      const htmlContents = await fetchExternalText(blobUrl, { signal });

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContents, 'text/html');
      const scrollingTextElement = doc.querySelector('.scrolling-text');
      if (scrollingTextElement) {
        setContent(scrollingTextElement.innerHTML);
      }

      const styleTags = doc.querySelectorAll('style');
      let extractedStyles = '';

      styleTags.forEach((styleTag) => {
        extractedStyles += styleTag.innerHTML;
      });
      setStyles(extractedStyles);
    } catch (error) {
      if (isRequestCanceled(error)) return;
      console.error('Error fetching the HTML content:', error);
    }
  };
  useEffect(() => {
    if (!isEmpty(systemInfo)) {
      const controller = new AbortController();
      getHtmlContent(systemInfo?.blobUrl, controller.signal);
      return () => controller.abort();
    }
    return undefined;
  }, [systemInfo]);
  //
  return (
    <Grid
      item
      container
      justifyContent="space-between"
      alignItems="bottom"
      display="flex"
      mt={-4}
      mb={-4}
    >
      <Grid item container direction="row" wrap="nowrap" justifyContent="flex-end">
        {!isEmpty(systemInfo) && systemInfo?.isSystemBannerEnabled && (
          <Box
            component={Grid}
            sx={{
              display: 'flex',
              width: '150%',
              height: 18,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              position: 'relative',
              zIndex: 10000,
              mt: 1,
            }}
          >
            <style>{styles}</style>
            <Typography className="scrolling-text" dangerouslySetInnerHTML={{ __html: content }} />
          </Box>
        )}
        <Grid item md={2} mt={1}>
          <Typography textAlign="right" sx={{ whiteSpace: 'nowrap' }}>
            Avl Rms:{' '}
            <Box
              component="span"
              sx={isHistoricalViOverlayActive ? HISTORICAL_VI_BLUR_STYLE : undefined}
            >
              {noOfRooms ?? 0}
            </Box>
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};
//
export default AnnouncementBlock;
